/**
 * Drops the keys whose value is `undefined`, so a cleared filter leaves the URL
 * entirely instead of lingering as `?source=`.
 *
 * Every route used to spell this out key by key — one `...(next.x === undefined
 * ? {} : { x: next.x })` line per field. That version was correct only for as
 * long as someone remembered to extend it, and `ExercisesRoute` even carried a
 * comment warning what happens otherwise: "one left out is a filter the toolbar
 * renders, writes and then silently drops on the way to the URL."
 *
 * Which is exactly what shipped. The food catalog's three curated-band filters
 * (meal role, prep time, cost) were added to the toolbar and not to the list, so
 * picking a value wrote it, the rebuild dropped it, the screen read its state
 * back from the URL and got nothing — the dropdown snapped to "all" and the
 * filter never reached the query either. A filter that silently does nothing
 * looks identical to a filter you never set, which is why it survived review.
 *
 * A hand-maintained allowlist cannot be made safe by being careful. This can
 * only forget a field if the field is not on the object.
 */
export function definedSearchParams<T extends object>(search: T): T {
  return Object.fromEntries(Object.entries(search).filter(([, value]) => value !== undefined)) as T;
}
